// #2121-spec-kits
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Badge, Button, UncontrolledTooltip } from 'reactstrap';
import { translate } from 'react-translate';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { actionGetKitTypes } from 'store/actions/kitsActions';
import { toast } from 'react-toastify';
import moment from 'moment';

const STATUS_TONE = {
  k_st_1:           'secondary',
  k_st_disassembled:         'secondary',
  k_st_2:                'success',
  k_st_reserved:             'info',
  k_st_3: 'info',
  k_st_4:  'primary',
  'k_st_5':      'warning',
  k_st_9:       'warning',
  k_st_6:              'danger',
  k_st_8:                'danger',
  k_st_10:               'warning',
  k_st_11:                   'dark',
  k_st_7:               'secondary',
};

const initials = (firstName, lastName) => {
  const f = (firstName || '').trim().charAt(0);
  const l = (lastName  || '').trim().charAt(0);
  return (f + l).toUpperCase() || '–';
};

const TablePatientKits = (props) => {
  const { data, t, clearKitAction } = props;
  const kitTypes = useSelector((s) => s.kitsReducer.types);
  const dispatch = useDispatch();
  const history = useHistory();

  const user = useSelector((s) => s.userReducer);

  useEffect(() => {
    if (!Object.keys(kitTypes).length) dispatch(actionGetKitTypes());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editPatientKit = (e, k) => {
    e.preventDefault();
    history.push(`/admin/kits/patients/details/${k._id}`);
  };

  const copyId = (e, val) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(val);
    toast.success(t('id-copied'));
  };

  return (
    <div className="kit-table-wrap">
      <table className="kit-table">
        <thead>
          <tr>
            <th style={{ width: '110px' }}>{t('friendly_id')}</th>
            <th>{t('practice')}</th>
            <th>{t('doctor')}</th>
            <th>{t('patient')}</th>
            <th style={{ width: '120px' }}>{t('type')}</th>
            <th style={{ width: '210px' }}>{t('status')}</th>
            <th style={{ width: '90px', textAlign: 'right' }}>{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {data.map((p, idx) => {
            const tone = STATUS_TONE[p.status] || 'secondary';
            const di = p.delivery_info || {};
            const bookingId = Object.keys(di).length && di.booking ? di.booking.booking_id : "";
            const doctor = di.doctor || {};
            const patient = di.patient || {};
            const practice = p.practice_data || {};
            const visibleAssets = (p.assets || []).slice(0, 6);
            const moreCount = (p.assets || []).length - visibleAssets.length;
            return (
              <React.Fragment key={p._id || idx}>
                <tr className={`kit-row tone-${tone}`}>
                  <td>
                    <div className="kit-id-cell">
                      <span className="kit-id-code">{p.friendly_id}</span>
                      <button
                        type="button"
                        className="kit-id-copy"
                        onClick={(e) => copyId(e, p.friendly_id)}
                        id={`tpk-copy-${idx}`}
                        aria-label="Copy ID"
                      >
                        <i className="now-ui-icons files_single-copy-04" />
                      </button>
                      <UncontrolledTooltip target={`tpk-copy-${idx}`}>{p.friendly_id}</UncontrolledTooltip>
                    </div>
                  </td>

                  <td>
                    <div className="kit-practice-cell">
                      <div className="kit-practice-name">{practice.name || practice.practice_name || '—'}</div>
                      {practice.owner && (
                        <div className="kit-practice-owner">
                          {practice.owner.firstName} {practice.owner.lastName}
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <div className="kit-person-cell">
                      <div className="kit-person-avatar tone-info">
                        {initials(doctor.firstName, doctor.lastName)}
                      </div>
                      <div className="kit-person-name">
                        {doctor.firstName} {doctor.lastName}
                      </div>
                    </div>
                  </td>

                  <td>
                    <div className="kit-person-cell">
                      <div className="kit-person-avatar tone-primary">
                        {initials(patient.firstName, patient.lastName)}
                      </div>
                      <div className="kit-person-name">
                        {patient.firstName} {patient.lastName}
                        {patient.ext_ref && <small className="kit-person-meta">ID {patient.ext_ref}</small>}
                      </div>
                    </div>
                  </td>

                  <td><span className="kit-type-chip">{p.type || '—'}</span></td>

                  <td>
                    <div className="kit-status-cell">
                      <Badge color={tone} pill className="kit-status-badge">{t(p.status)}</Badge>
                      {p.status_date && (
                        <time className="kit-status-time">
                          {moment(p.status_date).format('DD-MM-YYYY HH:mm:ss')}
                        </time>
                      )}
                    </div>
                  </td>

                  <td className="kit-actions-cell">
                    <div className="kit-actions">
                      <button
                        type="button"
                        className="kit-action-btn"
                        onClick={(e) => editPatientKit(e, p)}
                        id={`tpk-view-${idx}`}
                      >
                        <i className="fa fa-eye" />
                      </button>

                      {user.username === "kit-manager" ? (<>
                        <Button
                          size="sm"
                          onClick={() => {
                            clearKitAction(p._id);
                          }}
                        >
                          Clear kit
                        </Button>
                      </>) : (<></>)}

                      <UncontrolledTooltip target={`tpk-view-${idx}`}>{t('view-btn') || 'View'}</UncontrolledTooltip>
                    </div>
                  </td>
                </tr>
                <tr className="kit-row-assets">
                  <td colSpan={7}>
                    <div className="kit-assets-row">

                      {bookingId && bookingId.length ? (
                        <span className="kit-assets-label">{t('booking-id-label')}: <div className="kit-id-cell"><span className="kit-id-code">{bookingId}</span></div> </span>) 
                        : <></>
                      }

                      <span className="kit-assets-label">{t('assets')}</span>
                      {visibleAssets.length === 0 ? (
                        <span className="kit-cell-muted">{t('no-assets-short') || '—'}</span>
                      ) : (
                        visibleAssets.map((a) => (
                          <span className="kit-asset-chip" key={`a-${a._id || a}`}>
                            {a.asset_type ? `${a.asset_type}` : String(a)}
                            {a.brand && <small> · {a.brand}</small>}
                          </span>
                        ))
                      )}
                      {moreCount > 0 && (
                        <span className="kit-asset-more">+{moreCount}</span>
                      )}
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

TablePatientKits.defaultProps = { data: [], loading: true };
TablePatientKits.propTypes = { data: PropTypes.array, loading: PropTypes.bool };

export default translate('kits')(TablePatientKits);
